import {
  Component,
  computed,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { toSignal } from '@angular/core/rxjs-interop';
import { Subject, takeUntil } from 'rxjs';
import { Tab, RFI_TABS } from '@app/framework/underline-tabs/underline-tabs.types';
import { StatusBlockComponent } from '@app/pages/webapp/projects/view-project/rfi-listing/status-block/status-block.component';
import {
  SubmittalPackageOptions,
  SubmittalPackageParticipant,
} from '@app/store/submittals/submittals.types';
import { OptionsListSimpleComponent } from '@app/framework/overlays/options-list-simple/options-list-simple.component';
import { OptionsAction } from '@app/framework/overlays/option-list.constants';
import { NotificationV2Service } from '@app/notification-v2.service';
import { submittalsSelectors } from '@app/store/submittals/submittals.selectors';
import { InteractionBarStateService } from '@app/services/interaction-bar-state.service';
import { submittalPackageDetailsActions } from '@app/pages/webapp/projects/view-project/submittals/submittal-package-details-sidebar/submittal-package-details.actions';
import { SubmittalPackageDetailsTabComponent } from '@app/pages/webapp/projects/view-project/submittals/submittal-package-details-sidebar/submittal-package-details-tab/submittal-package-details-tab.component';
import { DiscussionsViewComponent } from '@app/framework/messages/discussions-view/discussions-view.component';
import { DiscussionThreadViewComponent } from '@app/framework/messages/discussion-thread-view/discussion-thread-view.component';
import { createOrUpdateMessage, messagesActions } from '@app/store/messages/messages.actions';
import { MESSAGING_VIEWS } from '@app/store/messages/messages.interfaces';
import { MessagingFooterService } from '@app/services/messaging-footer.service';
import { getMessagingView } from '@app/store/messages/messages.selectors';
import { ExpansionPanelV2Component } from '@app/framework/expansion-panel-v2/expansion-panel-v2.component';
import { LoadingSpinnerComponent } from '@app/framework/loading-animation/loading-spinner.component';
import { SubmittalPackageSubmittalsTabComponent } from '@app/pages/webapp/projects/view-project/submittals/submittal-package-details-sidebar/submittal-package-submittals-tab/submittal-package-submittals-tab.component';
import { TooltipDirective } from '@app/directives/tooltip.directive';
import { isNotOneOf, isOneOf } from '@app/framework/helpers/general.helper';
import { UnderlineTabsComponent } from '@app/framework/underline-tabs/underline-tabs.component';

@Component({
  selector: 'app-submittal-package-details-sidebar',
  imports: [
    UnderlineTabsComponent,
    StatusBlockComponent,
    OptionsListSimpleComponent,
    SubmittalPackageDetailsTabComponent,
    DiscussionsViewComponent,
    DiscussionThreadViewComponent,
    ExpansionPanelV2Component,
    LoadingSpinnerComponent,
    SubmittalPackageSubmittalsTabComponent,
    TooltipDirective,
  ],
  templateUrl: './submittal-package-details-sidebar.component.html',
  styleUrl: './submittal-package-details-sidebar.component.scss',
})
export class SubmittalPackageDetailsSidebarComponent implements OnInit, OnDestroy {
  readonly store = inject(Store);
  readonly notif = inject(NotificationV2Service);
  private readonly interactionBar = inject(InteractionBarStateService);
  private readonly messagesFooterService = inject(MessagingFooterService);

  packageId = input<number>();
  submittalPackage = toSignal(this.store.select(submittalsSelectors.selectPackageDetails));
  isPackageLoading = toSignal(this.store.select(submittalsSelectors.selectPackageDetailsLoading));

  selectedMessagingView = toSignal(this.store.select(getMessagingView));
  MESSAGING_VIEWS = signal(MESSAGING_VIEWS).asReadonly();
  isExpanded = signal<Record<string, boolean>>({});
  private isDestroyed$ = new Subject<void>();

  tabs = signal<Tab[]>([
    { value: RFI_TABS.PACKAGE_DETAILS, icon: 'icon-folded-document-info' },
    { value: RFI_TABS.SUBMITTALS, icon: 'icon-document-shield' },
    { value: RFI_TABS.DISCUSSIONS, icon: 'icon-discussions-bubble' },
    // { value: TAB_OPTION.TRANSMITTAL_LOG, icon: 'icon-folded-word-doc' },
  ]);
  TAB_OPTION = RFI_TABS;

  selectedTab = signal<RFI_TABS>(RFI_TABS.PACKAGE_DETAILS);

  packageItems = computed(() => {
    return this.submittalPackage()?.entries;
  });

  version = computed(() => {
    return `${String(this.submittalPackage()?.spec_version).padStart(2, '0')}.${String(this.submittalPackage()?.package_version).padStart(2, '0')}`;
  });

  availableVersions = computed(() => {
    return this.submittalPackage()?.package_versions ?? [];
  });

  hasLowerVersion = computed(() => {
    const currentVersionIndex = this.availableVersions().findIndex(
      (version) => version.id === this.submittalPackage()?.id,
    );
    return currentVersionIndex > 0;
  });
  hasHigherVersion = computed(() => {
    const currentVersionIndex = this.availableVersions().findIndex(
      (version) => version.id === this.submittalPackage()?.id,
    );
    return currentVersionIndex >= 0 && currentVersionIndex < this.availableVersions().length - 1;
  });

  requestSubmittalsAllowed = computed(() => {
    const sentForApproval = !!this.submittalPackage()?.requested_approval_at;
    const sentToOwner = !!this.submittalPackage()?.sent_to_owner_at;
    const hasRequestedSubmittals = !!this.submittalPackage()?.requested_submittals_at;
    const hasReceivedSubmittals = !!this.submittalPackage()?.received_submittals_at;

    return !sentForApproval && !sentToOwner && !hasRequestedSubmittals && !hasReceivedSubmittals;
  });

  submittalCompanies = computed(() => {
    const submittalPackage = this.submittalPackage();
    if (!submittalPackage) {
      return [];
    }

    const companies = new Map<string, Set<SubmittalPackageParticipant>>();

    // Add manager
    if (submittalPackage.package_manager) {
      const managerCompany = submittalPackage.package_manager.company_name;
      if (!companies.has(managerCompany)) {
        companies.set(managerCompany, new Set());
      }
      companies.get(managerCompany)!.add({
        user_id: submittalPackage.package_manager.id,
        name: submittalPackage.package_manager.name,
        company_name: submittalPackage.package_manager.company_name,
      });
    }

    // Add approver
    if (submittalPackage.package_approver) {
      const approverCompany = submittalPackage.package_approver.company_name;
      if (!companies.has(approverCompany)) {
        companies.set(approverCompany, new Set());
      }
      companies.get(approverCompany)!.add({
        user_id: submittalPackage.package_approver.id,
        name: submittalPackage.package_approver.name,
        company_name: submittalPackage.package_approver.company_name,
      });
    }

    // Add participants
    submittalPackage.participants?.forEach((participant) => {
      const company = participant.company_name;
      if (!companies.has(company)) {
        companies.set(company, new Set());
      }
      companies.get(company)!.add(participant);
    });

    return Array.from(companies.entries()).map(([company_name, users]) => ({
      company_name,
      users: Array.from(users),
    }));
  });

  protected readonly menuOptions = computed(() => {
    const options = this.getBaseMenuOptions();
    return this.filterOptionsByStatusAndRole(options);
  });

  effectRef = effect(() => {
    const groupId = this.submittalPackage()?.messaging_group?.id;
    if (!groupId) {
      console.warn(
        'Submittal Package Details Sidebar: No messaging group associated with this package',
      );
      return;
    }
    this.store.dispatch(messagesActions.setGroupFilters({ filters: { group_id: groupId } }));
    this.store.dispatch(messagesActions.loadFirstPageOfGroups({ clearOldGroups: false }));
    this.store.dispatch(
      messagesActions.setMessagingView({ view: MESSAGING_VIEWS.DISCUSSION_VIEW }),
    );
    this.store.dispatch(messagesActions.setSelectedGroupId({ groupId }));
    this.store.dispatch(messagesActions.loadMessages());
  });

  ngOnInit() {
    this.store.dispatch(
      submittalPackageDetailsActions.onPackageDetailsOpen({ packageId: this.packageId() }),
    );

    this.messagesFooterService.save.pipe(takeUntil(this.isDestroyed$)).subscribe(() => {
      this.store.dispatch(createOrUpdateMessage({}));
    });
  }

  ngOnDestroy() {
    this.store.dispatch(submittalPackageDetailsActions.onPackageDetailsClose());
    this.isDestroyed$.next();
    this.isDestroyed$.complete();
  }

  protected onMenuOptionSelected(option: OptionsAction<string>) {
    const selectedOption = option as OptionsAction<SubmittalPackageOptions>;

    switch (selectedOption.value) {
      case 'cancel_request': {
        return this.cancelRequestSubmittals();
      }
      case 'request_submittals': {
        return this.requestSubmittals();
      }
      case 'send_for_approval': {
        return this.sendForApproval();
      }
      case 'cancel_approval': {
        return this.cancelApproval();
      }
      case 'mark_as_complete': {
        return this.markAsComplete();
      }
      case 'mark_as_ready': {
        return this.markAsReady();
      }
      case 'send_to_owner': {
        return this.sendToOwner();
      }
      case 'cancel_send_to_owner': {
        return this.cancelSendToOwner();
      }
      case 'create_revision': {
        return this.createRevision();
      }
      case 'edit_package': {
        this.interactionBar.openSubmittalPackageSidebar({ packageId: this.packageId() });
        break;
      }
      case 'void': {
        return this.voidPackage();
      }
      case 'close': {
        return this.closePackage();
      }
      case 'reopen': {
        return this.reopenPackage();
      }
      case 'delete': {
        return this.deletePackage();
      }
      default: {
        this.notif.showInfo('This option is not implemented yet.');
        break;
      }
    }
  }
  private requestSubmittals() {
    this.notif
      .showConfirmDialog('Are you sure you want to request submittals for this package?')
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.store.dispatch(
            submittalPackageDetailsActions.requestSubmittals({
              packageId: this.packageId(),
            }),
          );
        }
      });
  }

  private cancelRequestSubmittals() {
    this.notif
      .showConfirmDialog('Are you sure you want to cancel the submittal request?')
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.store.dispatch(
            submittalPackageDetailsActions.cancelSubmittalRequest({
              packageId: this.packageId(),
            }),
          );
        }
      });
  }

  private sendForApproval() {
    this.notif
      .showConfirmDialog('Are you sure you want to send this package for approval?')
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.store.dispatch(
            submittalPackageDetailsActions.sendPackageForApproval({
              packageId: this.packageId(),
            }),
          );
        }
      });
  }

  private cancelApproval() {
    this.notif
      .showConfirmDialog(
        'Are you sure you want to cancel approval? This will retrieve the package from the approver.',
      )
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.store.dispatch(
            submittalPackageDetailsActions.cancelPackageApproval({
              packageId: this.packageId(),
            }),
          );
        }
      });
  }

  private markAsComplete() {
    this.notif
      .showConfirmDialog('Are you sure you want to mark this package as complete?')
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.store.dispatch(
            submittalPackageDetailsActions.markAsComplete({
              packageId: this.packageId(),
            }),
          );
        }
      });
  }

  private markAsReady() {
    this.notif
      .showConfirmDialog('Are you sure you want to mark this package as ready?')
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.store.dispatch(
            submittalPackageDetailsActions.markAsReady({
              packageId: this.packageId(),
            }),
          );
        }
      });
  }

  private sendToOwner() {
    this.notif
      .showConfirmDialog('Are you sure you want to send this package to the owner for review?')
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.store.dispatch(
            submittalPackageDetailsActions.submitToOwner({
              packageId: this.packageId(),
            }),
          );
        }
      });
  }
  private cancelSendToOwner() {
    this.notif
      .showConfirmDialog('Are you sure you want to cancel sending this package to the owner?')
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.store.dispatch(
            submittalPackageDetailsActions.cancelSubmitToOwner({
              packageId: this.packageId(),
            }),
          );
        }
      });
  }

  private createRevision() {
    this.notif
      .showConfirmDialog('Are you sure you want to create a revision for this package?')
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.store.dispatch(
            submittalPackageDetailsActions.createRevision({
              packageId: this.packageId(),
            }),
          );
        }
      });
  }

  private voidPackage() {
    this.notif
      .showConfirmDialog('Are you sure you want to void this package?')
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.store.dispatch(
            submittalPackageDetailsActions.voidPackage({
              packageId: this.packageId(),
            }),
          );
        }
      });
  }

  private closePackage() {
    this.notif
      .showConfirmDialog('Are you sure you want to close this package?')
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.store.dispatch(
            submittalPackageDetailsActions.closePackage({
              packageId: this.packageId(),
            }),
          );
        }
      });
  }

  private reopenPackage() {
    this.notif
      .showConfirmDialog('Are you sure you want to reopen this package?')
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.store.dispatch(
            submittalPackageDetailsActions.reopenPackage({
              packageId: this.packageId(),
            }),
          );
        }
      });
  }

  private deletePackage() {
    this.notif
      .showConfirmDialog('Are you sure you want to delete this package?')
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.store.dispatch(
            submittalPackageDetailsActions.deletePackage({
              packageId: this.packageId(),
            }),
          );
        }
      });
  }

  protected toggleCompanyExpand(company: { company_name: string }) {
    this.isExpanded.update((state) => ({
      ...state,
      [company.company_name]: !state[company.company_name],
    }));
  }

  /**
   * Show a different version of the same submittal package. <br/>
   * If there is no higher/lower version, nothing happens.
   * @param direction can be positive (next version) or negative (previous version)
   */
  protected changeVersion(direction: number) {
    const versions = this.availableVersions();
    const currentVersionIndex = versions.findIndex((v) => v?.id === this.submittalPackage()?.id);
    if (currentVersionIndex < 0 || versions.length < 2) {
      return;
    }

    let newVersionId: number = null;
    if (direction > 0 && this.hasHigherVersion()) {
      newVersionId = versions[currentVersionIndex + 1]?.id;
    } else if (direction < 0 && this.hasLowerVersion()) {
      newVersionId = versions[currentVersionIndex - 1]?.id;
    }

    if (!newVersionId) {
      return;
    }

    this.store.dispatch(
      submittalPackageDetailsActions.newVersionSelected({ packageId: newVersionId }),
    );
  }

  /**
   * Returns all possible menu options before filtering by status and role.
   * @private
   */
  private getBaseMenuOptions(): OptionsAction<SubmittalPackageOptions>[] {
    if (!this.submittalPackage()) {
      return [];
    }

    return [
      this.submittalPackage().requested_submittals_at
        ? {
            value: 'cancel_request',
            label: 'Cancel Request',
            class: 'border-b border-navy-300',
          }
        : {
            value: 'request_submittals',
            label: 'Request Submittals',
            disabled: !this.requestSubmittalsAllowed(),
            tooltip: this.requestSubmittalsAllowed()
              ? ''
              : 'Cannot request submittals when package items have status or source files exist',
            class: 'border-b border-navy-300',
          },
      this.submittalPackage()?.requested_approval_at
        ? {
            value: 'cancel_approval',
            label: 'Cancel Approval',
            class: 'border-b border-navy-300',
          }
        : {
            value: 'send_for_approval',
            label: 'Send for Approval',
            class: 'border-b border-navy-300',
          },
      {
        value: 'mark_as_complete',
        label: 'Mark as Complete',
        class: 'border-b border-navy-300',
      },
      {
        value: 'mark_as_ready',
        label: 'Mark as Ready',
        class: 'border-b border-navy-300',
      },
      this.submittalPackage()?.sent_to_owner_at
        ? {
            value: 'cancel_send_to_owner',
            label: 'Undo to Owner',
            class: 'border-b border-navy-300',
          }
        : {
            value: 'send_to_owner',
            label: 'Send to Owner',
            class: 'border-b border-navy-300',
          },
      {
        value: 'create_revision',
        label: 'Create Revision',
        class: 'border-b border-navy-300',
      },
      {
        value: 'close',
        label: 'Close Package',
        class: 'border-b border-navy-300',
      },
      {
        value: 'edit_package',
        label: 'Edit Package',
      },
      {
        value: 'download_cover',
        label: 'Download Cover',
        tooltip: 'Coming soon',
        disabled: true,
      },
      {
        value: 'void',
        label: 'Void',
      },
      {
        value: 'reopen',
        label: 'Reopen',
      },
      {
        value: 'delete',
        label: 'Delete',
      },
    ];
  }

  /**
   * Filters the options based on user role in package, applicability and package status.
   * @private
   */
  private filterOptionsByStatusAndRole(
    options: OptionsAction<SubmittalPackageOptions>[],
  ): OptionsAction<SubmittalPackageOptions>[] {
    let filteredOptions = this.filterOptionsByStatus(options);
    filteredOptions = this.filterOptionsByRole(filteredOptions);
    filteredOptions = this.filterNonApplicableOptions(filteredOptions);
    return filteredOptions;
  }

  private filterOptionsByStatus(
    options: OptionsAction<SubmittalPackageOptions>[],
  ): OptionsAction<SubmittalPackageOptions>[] {
    const status = this.submittalPackage()?.status;

    if (status === 'open') {
      return options.filter((option) => option.value !== 'reopen');
    } else if (status === 'closed') {
      return options.filter((option) =>
        isNotOneOf<SubmittalPackageOptions>(option.value, [
          'close',
          'request_submittals',
          'send_for_approval',
          'cancel_approval',
          'mark_as_complete',
          'send_to_owner',
          'create_revision',
          'edit_package',
          'void',
          'send_to_owner',
          'cancel_send_to_owner',
        ]),
      );
    } else if (status === 'void') {
      return options.filter((option) =>
        isNotOneOf<SubmittalPackageOptions>(option.value, [
          'close',
          'request_submittals',
          'send_for_approval',
          'cancel_approval',
          'mark_as_complete',
          'send_to_owner',
          'edit_package',
          'void',
        ]),
      );
    }

    return options;
  }

  private filterOptionsByRole(
    options: OptionsAction<SubmittalPackageOptions>[],
  ): OptionsAction<SubmittalPackageOptions>[] {
    const role = this.submittalPackage()?.role;

    if (role === 'approver') {
      if (!this.submittalPackage()?.received_approval_at) {
        return options.filter((option) =>
          isOneOf<SubmittalPackageOptions>(option.value, ['mark_as_complete', 'download_cover']),
        );
      }
      return options.filter((option) =>
        isOneOf<SubmittalPackageOptions>(option.value, ['download_cover']),
      );
    } else if (role === 'owner') {
      return options.filter((option) =>
        isOneOf<SubmittalPackageOptions>(option.value, ['mark_as_ready', 'download_cover']),
      );
    } else if (role === 'participant') {
      return options.filter((option) =>
        isOneOf<SubmittalPackageOptions>(option.value, ['download_cover']),
      );
    }

    // manager
    return options.filter((option) =>
      isNotOneOf<SubmittalPackageOptions>(option.value, ['mark_as_complete', 'mark_as_ready']),
    );
  }

  private filterNonApplicableOptions(
    options: OptionsAction<SubmittalPackageOptions>[],
  ): OptionsAction<SubmittalPackageOptions>[] {
    // if requested approval or sent to owner there's no reason to request again submittals.
    if (
      this.submittalPackage()?.received_submittals_at ||
      this.submittalPackage()?.requested_approval_at ||
      this.submittalPackage()?.sent_to_owner_at
    ) {
      options = options.filter((option) =>
        isNotOneOf<SubmittalPackageOptions>(option.value, [
          'cancel_request',
          'request_submittals',
          'mark_as_ready',
        ]),
      );
    }

    // cannot send for approval or cancel it if already received approval
    if (this.submittalPackage()?.received_approval_at) {
      options = options.filter((option) =>
        isNotOneOf<SubmittalPackageOptions>(option.value, [
          'cancel_approval',
          'send_for_approval',
          'mark_as_complete',
        ]),
      );
    }

    // cannot send to owner if already sent
    if (this.submittalPackage()?.sent_to_owner_at) {
      options = options.filter((option) =>
        isNotOneOf<SubmittalPackageOptions>(option.value, ['send_to_owner']),
      );
    }

    return options;
  }
}
